import { createRouter, createWebHistory } from 'vue-router'
import Signup from './views/Signup.vue'
import Login from './views/Login.vue'
import ForgotPassword from './views/ForgotPassword.vue'
import ResetPassword from './views/ResetPassword.vue'
import Dashboard from './views/Dashboard.vue'
import HomeView from './views/HomeView.vue'
import Logout from './views/Logout.vue'
import GoogleAuthComplete from './views/GoogleAuthComplete.vue'
import GoogleSignup from './views/GoogleSignup.vue'
import FrontPageView from './views/FrontPageView.vue'
import FilmDetail from './views/FilmDetail.vue'
import ShowDetail from './views/ShowDetail.vue'
import SeasonDetail from './views/SeasonDetail.vue'
import EpisodeDetail from './views/EpisodeDetail.vue'
import PersonDetail from './views/PersonDetail.vue'
import FilmListDetail from './views/FilmListDetail.vue'
import ShowListDetail from './views/ShowListDetail.vue'
import EpisodeListDetail from './views/EpisodeListDetail.vue'
import PersonListDetail from './views/PersonListDetail.vue'
import DiscoverView from './views/DiscoverView.vue'
import SearchView from './views/SearchView.vue'
import LegalPage from './views/LegalPage.vue'
import ConfirmEmail from './views/ConfirmEmail.vue'
import NotFoundView from './views/NotFoundView.vue'
import StaticContentRedirect from './views/StaticContentRedirect.vue'
import { safeRedirectPath } from './utils/auth'

async function requireExistingPublicProfile(to) {
  const username = String(to.params.username || '').trim()
  if (!username) {
    return {
      name: 'not-found',
      params: { pathMatch: to.path.replace(/^\/+/, '').split('/') },
    }
  }

  try {
    const response = await fetch(`/api/v1/home/public/${encodeURIComponent(username)}`, {
      headers: { Accept: 'application/json' },
    })
    if (response.status === 404) {
      return {
        name: 'not-found',
        params: { pathMatch: to.path.replace(/^\/+/, '').split('/') },
      }
    }
  } catch {
    // Let the profile view handle transient API failures without hiding valid profiles.
  }

  return true
}

export const routes = [
  { path: '/', name: 'front-page', component: FrontPageView },
  { path: '/sign-up', name: 'signup', component: Signup },
  { path: '/signup', redirect: '/sign-up' },
  { path: '/login', name: 'login', component: Login },
  { path: '/auth/google/complete', name: 'google-auth-complete', component: GoogleAuthComplete },
  { path: '/auth/google/signup', name: 'google-signup', component: GoogleSignup },
  { path: '/forgot-password', name: 'forgot-password', component: ForgotPassword },
  { path: '/reset-password/:uidb64/:token', name: 'reset-password', component: ResetPassword },
  { path: '/logout', name: 'logout', component: Logout },
  {
    path: '/home/films/:filmStatus(watched|rated|favourites|watchlist)/:filterA?/:filterB?',
    name: 'home-films',
    component: HomeView,
    meta: { requiresAuth: true },
  },
  {
    path: '/home/:contentType(films|shows|seasons|episodes|people|lists)/:itemStatus(watched|rated|favourites|watchlist|next)?/:filterA?/:filterB?',
    name: 'home-content',
    component: HomeView,
    meta: { requiresAuth: true },
  },
  {
    path: '/home/:medium(TV)?/:section(stats|settings)?',
    name: 'home',
    component: HomeView,
    meta: { requiresAuth: true },
  },
  {
    path: '/filmsomniacs/demo-account/:contentType(films|shows|seasons|episodes|people|lists)?/:itemStatus(watched|rated|favourites|watchlist|next)?/:filterA?/:filterB?',
    name: 'demo-profile',
    component: HomeView,
  },
  {
    path: '/filmsomniacs/:username/:contentType(films|episodes|lists)?/:itemStatus(watched)?/:filterA?',
    name: 'public-profile',
    component: HomeView,
    beforeEnter: requireExistingPublicProfile,
  },
  { path: '/dashboard', name: 'dashboard', component: Dashboard, meta: { requiresAuth: true } },
  { path: '/discover/:tab?/:section?', name: 'discover', component: DiscoverView },
  { path: '/search', name: 'search', component: SearchView },
  { path: '/tos', name: 'terms-of-service', component: LegalPage },
  { path: '/privacy', name: 'privacy-policy', component: LegalPage },
  { path: '/cookies', name: 'cookie-policy', component: LegalPage },
  { path: '/contact', name: 'contact-information', component: LegalPage },
  { path: '/movie-diary', name: 'movie-diary', component: StaticContentRedirect },
  { path: '/tv-show-tracker', name: 'tv-show-tracker', component: StaticContentRedirect },
  { path: '/every-watch-counts', name: 'every-watch-counts', component: StaticContentRedirect },
  { path: '/confirm-email/:uidb64/:token', name: 'confirm-email', component: ConfirmEmail },
  { path: '/lists/film/:listSlug/comments', name: 'film-list-section', component: FilmListDetail },
  { path: '/lists/film/:listSlug', name: 'film-list-detail', component: FilmListDetail },
  { path: '/lists/show/:listSlug/comments', name: 'show-list-section', component: ShowListDetail },
  { path: '/lists/show/:listSlug', name: 'show-list-detail', component: ShowListDetail },
  { path: '/lists/episode/:listSlug/comments', name: 'episode-list-section', component: EpisodeListDetail },
  { path: '/lists/episode/:listSlug', name: 'episode-list-detail', component: EpisodeListDetail },
  { path: '/lists/person/:listSlug/comments', name: 'person-list-section', component: PersonListDetail },
  { path: '/lists/person/:listSlug', name: 'person-list-detail', component: PersonListDetail },
  { path: '/films/:slug/:section(credits|lists|comments|discussion|activity|you)/:feed?', name: 'film-section', component: FilmDetail },
  { path: '/films/:slug', name: 'film-detail', component: FilmDetail },
  { path: '/people/:slug/:section(directing|writing|crew|TV|lists|comments)', name: 'person-section', component: PersonDetail },
  { path: '/people/:slug', name: 'person-detail', component: PersonDetail },
  {
    path: '/shows/:showSlug/season/:seasonNumber/episode/:episodeNumber/:episodeTitleSlug/:section(lists|comments|discussion|activity|you)?',
    name: 'episode-detail',
    component: EpisodeDetail,
  },
  { path: '/shows/:slug/season/:seasonNumber/:section(comments|discussion|activity|you)?', name: 'season-detail', component: SeasonDetail },
  { path: '/shows/:slug/:section(seasons|credits|lists|comments|discussion|activity)/:feed?', name: 'show-section', component: ShowDetail },
  { path: '/shows/:slug', name: 'show-detail', component: ShowDetail },
  { path: '/app/:pathMatch(.*)*', redirect: '/sign-up' },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFoundView },
]

export function createFilmsomniacRouter({ history = createWebHistory('/') } = {}) {
  return createRouter({
    history,
    routes,
  })
}

export function installAuthGuards(router, auth) {
  router.beforeEach((to, from, next) => {
    if (to.name === 'front-page' && auth.isAuthenticated.value) {
      return next('/home')
    }
    if (to.meta.requiresAuth && !auth.isAuthenticated.value) {
      auth.clearTokens()
      return next({ path: '/login', query: { redirect: to.fullPath, auth_redirect: '1' } })
    }
    if ((to.path === '/login' || to.path === '/signup' || to.path === '/sign-up') && auth.isAuthenticated.value) {
      const target = safeRedirectPath(to.query.redirect, '/home')
      return next(target)
    }
    return next()
  })
}
